import { Box, Grid } from "@mui/material";
import { FC } from "react";
import FormTextInput from "./FormComponents/FormTextInput";
import FormSelectInput from "./FormComponents/FormSelectInput";
import FormCheckbox from "./FormComponents/FormCheckBox";
import {
  IFormField,
  deepDerefrencerValidations,
} from "../../utils/form_factory";
import { FormDateInput } from "./FormComponents/FormDateInput";
import FormTextArea from "./FormComponents/FormTextArea";
import FormPhoneInput from "./FormComponents/FormPhoneInput";
import FormFileInput from "./FormComponents/FormFileInput";
import FormClientAccount from "./FormComponents/FormClientAccount";
import FormSelectSearchInput from "./FormComponents/FormSelectSearch";
import FormImageCropper from "./FormComponents/FormImageCropper";
import { FormDateTimeInput } from "./FormComponents/FormDateTimeInput";
import FormBranchField from "./FormComponents/FormBranchField";
import FormNinInput from "./FormComponents/FormNinInput";
import FormAsyncSelectSearch from "./FormComponents/FormAsyncSelectSearch";
import FormMultiSelectSearchInput from "./FormComponents/FormMultiSelectSearch";
import FormIppnsInput from "./FormComponents/FormIppnsInput";
import FormRadioBtn from "./FormComponents/FormRadioBtn";
import ConcatenatedTextSelect from "./FormComponents/InputGroupSelect";
import ConcatenatedSelectSelect from "./FormComponents/SelectGroupSelect";
import InputGroupText from "./FormComponents/InputGroupText";
import MultiInputField from "./FormComponents/MultiInputField";
import FormInputTextAutoCompletion from "./FormComponents/FormInputTextWithAutoCompletion";
import FlexBox from "../FlexBox";
import VerificationButton, {
  BtnDisplayType,
  VerificationType,
} from "./KYC/VerificationButton/VerificationButton";
import IsGranted from "../../guards/IsGranted";
import { PERMISSIONS } from "../../constants/permissions";
import FormMultiSelectCheck from "./FormComponents/FormMultiSelectCheck";
import FormMultiSelect from "./FormComponents/FormMultiSelect";
import { IConfigurationFormField } from "./FormComponents/FormConfiguration/settings";
import * as Yup from "yup";
import "yup-phone-lite";
import RequiredFieldLabel from "./RequiredFieldLabel";
import TableFormField from "./FormComponents/TableFormField";
import { FormInputGeoLocator } from "./FormComponents/FormGeoLocationIput";
import FormMultiSelectEmail from "./FormComponents/FormMultiSelectEmail";
interface IFormFactoryProps {
  formikInstance: any;
  formFields: IFormField[];
  validationSchema?: any;
  others?: any;
  isCustomFormInstance?: boolean;
  configurationFormFields?: IConfigurationFormField[];
  isHidden?: boolean;
}

const isFieldRequiredCustom = (
  fieldName: string,
  configurationFormFields: IConfigurationFormField[],
  validationSchema: any,
) => {
  if (configurationFormFields?.length) {
    return formFieldIsRequired(fieldName, configurationFormFields);
  }
  return deepDerefrencerValidations(validationSchema, fieldName)?.tests?.some(
    (test: any) => test.OPTIONS.name === "required",
  );
};

export const CustomInputLabel = (
  formField: any,
  configurationFormFields: any,
  validationSchema: any,
) => {
  return (
    <>
      {isFieldRequiredCustom(
        formField.name,
        configurationFormFields,
        validationSchema,
      ) ? (
        <>
          <RequiredFieldLabel
            label={getFieldLabel(formField, configurationFormFields)}
          />
        </>
      ) : (
        <> {getFieldLabel(formField, configurationFormFields)} </>
      )}
    </>
  );
};

export const getFieldLabel = (
  field: IFormField,
  configurationFields?: IConfigurationFormField[],
) => {
  const fieldConfiguration = configurationFields?.find(
    (item: any) => item.name == field?.name,
  );
  if (!fieldConfiguration) return field?.label;
  return fieldConfiguration?.custom_label || field?.label;
};

export const formFieldIsRequired = (
  fieldName: string,
  configurationFormFields: IConfigurationFormField[],
) => {
  const fieldSettings = configurationFormFields?.find(
    (field: any) => field?.name == fieldName,
  );
  return fieldSettings?.required ? true : false;
};

export const getFieldByLabelName = (
  fieldName: string,
  configurationFormFields: IConfigurationFormField[],
  defaultName?: string,
) => {
  if (configurationFormFields?.length) {
    const fieldSettings = configurationFormFields?.find(
      (field: any) => field?.name == fieldName,
    );
    return fieldSettings?.custom_label || fieldSettings?.label;
  }
  return defaultName || "";
};

export const formFieldValidationSchema = ({
  fieldName,
  configurationFormFields,
  fieldType,
  customMessage,
  validate = true,
  phoneCode = "UG",
}: {
  fieldName: string;
  configurationFormFields: IConfigurationFormField[];
  fieldType:
    | "string"
    | "object"
    | "number"
    | "email"
    | "phone"
    | "nin"
    | "ipps_no";
  validate?: boolean;
  customMessage?: string;
  phoneCode?: any;
}) => {
  /**
   * IF WE ARE VALIDATING PHONE NUMBER
   */
  if (fieldType == "phone") {
    return validate &&
      phoneCode &&
      formFieldIsRequired(fieldName, configurationFormFields)
      ? Yup.string()
          .phone(
            phoneCode,
            `${getFieldByLabelName(
              fieldName,
              configurationFormFields,
            )} must be a valid phone number of ${phoneCode} region`,
          )
          .required(
            fieldName &&
              ` ${getFieldByLabelName(
                fieldName,
                configurationFormFields,
              )} is required`,
          )
      : Yup.string().phone(
          phoneCode,
          `${getFieldByLabelName(
            fieldName,
            configurationFormFields,
          )} must be a valid phone number of ${phoneCode} region`,
        );
  } else if (fieldType == "nin") {
    // let validator = (Yup as any)[fieldType]();
    /**
     * IF WE ARE VALIDATING NIN
     */
    return validate && formFieldIsRequired(fieldName, configurationFormFields)
      ? Yup.string()
          .required(
            fieldName &&
              ` ${getFieldByLabelName(
                fieldName,
                configurationFormFields,
              )} is required`,
          )
          .max(
            14,
            `${getFieldByLabelName(
              fieldName,
              configurationFormFields,
            )} should be 14 characters long`,
          )
      : Yup.string()
          .max(
            14,
            `${getFieldByLabelName(
              fieldName,
              configurationFormFields,
            )} should be 14 characters long`,
          )
          .nullable();
  } else if (fieldType == "email") {
    /**
     * IF WE ARE VALIDATING EMAIL
     */

    return validate && formFieldIsRequired(fieldName, configurationFormFields)
      ? Yup.string()
          .required(
            `${getFieldByLabelName(
              fieldName,
              configurationFormFields,
            )} is required`,
          )
          .email("Invalid Email")
      : Yup.string().email("Invalid Email").nullable();
  } else if (fieldType == "number") {
    /**
     * IF WE ARE VALIDATING A NUMBER FIELD
     */

    return validate && formFieldIsRequired(fieldName, configurationFormFields)
      ? Yup.number()
          .required(
            `${getFieldByLabelName(
              fieldName,
              configurationFormFields,
            )} is required`,
          )
          .typeError(
            `${getFieldByLabelName(
              fieldName,
              configurationFormFields,
            )} is required`,
          )
      : Yup.number()
          .typeError(
            `${getFieldByLabelName(
              fieldName,
              configurationFormFields,
            )} is required`,
          )
          .nullable();
  } else if (fieldType == "object") {
    /**
     * IF WE ARE VALIDATING AN OBJECT FIELD
     */

    return validate && formFieldIsRequired(fieldName, configurationFormFields)
      ? Yup.object().required(
          `${getFieldByLabelName(
            fieldName,
            configurationFormFields,
          )} is required`,
        )
      : Yup.object().nullable();
  } else if (fieldType == "ipps_no") {
    /**
     * IF WE ARE VALIDATING IPPS NO
     */

    return validate && formFieldIsRequired(fieldName, configurationFormFields)
      ? Yup.string()
          .required(
            `${getFieldByLabelName(
              fieldName,
              configurationFormFields,
            )} is required`,
          )
          .length(
            15,
            `${getFieldByLabelName(
              fieldName,
              configurationFormFields,
            )} must be exactly 15 digits long`,
          )
          .matches(
            /^[0-9]+$/,
            `${getFieldByLabelName(
              fieldName,
              configurationFormFields,
            )} must only contain digits`,
          )
      : Yup.string()
          .length(
            15,
            `${getFieldByLabelName(
              fieldName,
              configurationFormFields,
            )} must be exactly 15 digits long`,
          )
          .matches(
            /^[0-9]+$/,
            `${getFieldByLabelName(
              fieldName,
              configurationFormFields,
            )} must only contain digits`,
          );
  } else {
    return validate && formFieldIsRequired(fieldName, configurationFormFields)
      ? Yup.string()
          .required(
            `${getFieldByLabelName(
              fieldName,
              configurationFormFields,
            )} is required`,
          )
          .typeError(
            `${getFieldByLabelName(
              fieldName,
              configurationFormFields,
            )} is required`,
          )
      : Yup.string().nullable();
  }
};

export const FormTypeFactory = (
  formField: IFormField,
  formikInstance: any,
  validationSchema: any,
  others: any,
  configurationFormFields?: IConfigurationFormField[],
) => {
  const isFieldRequired = (
    fieldName: string,
    configurationFormFields?: IConfigurationFormField[],
  ) => {
    if (configurationFormFields?.length) {
      return formFieldIsRequired(fieldName, configurationFormFields);
    }
    return deepDerefrencerValidations(validationSchema, fieldName)?.tests?.some(
      (test: any) => test.OPTIONS.name === "required",
    );
  };

  const InputLabel = (formField: any, configurationFormFields?: any) => {
    return (
      <>
        {isFieldRequired(formField.name, configurationFormFields) ? (
          <>
            <RequiredFieldLabel
              label={getFieldLabel(formField, configurationFormFields)}
            />
          </>
        ) : (
          <> {getFieldLabel(formField, configurationFormFields)} </>
        )}
      </>
    );
  };

  switch (formField.uiType) {
    case "text":
      return (
        <FormTextInput
          {...formField}
          type={formField.type}
          formControl={formikInstance}
          label={InputLabel(formField, configurationFormFields)}
        />
      );
    case "ippns":
      return (
        <FormIppnsInput
          {...formField}
          type={formField.type}
          formControl={formikInstance}
          label={InputLabel(formField, configurationFormFields)}
        />
      );
    case "phone":
      return (
        <FormPhoneInput
          {...formField}
          formControl={formikInstance}
          label={`${getFieldLabel(formField, configurationFormFields)} ${
            isFieldRequired(formField.name, configurationFormFields) ? "*" : ""
          }`}
          countryCode={formField?.countryCode}
        />
      );
    case "nin":
      return (
        <FormNinInput
          {...formField}
          formControl={formikInstance}
          label={InputLabel(formField, configurationFormFields)}
        />
      );
    case "file":
      return (
        <FormFileInput
          {...formField}
          formControl={formikInstance}
          label={InputLabel(formField, configurationFormFields)}
        />
      );
    case "file-cropper":
      return (
        <FormImageCropper
          {...formField}
          formControl={formikInstance}
          label={InputLabel(formField, configurationFormFields)}
        />
      );
    case "client-account":
      return (
        <FormClientAccount
          {...formField}
          multipleAccounts={formField.multipleAccounts ? true : false}
          formControl={formikInstance}
          label={InputLabel(formField, configurationFormFields)}
        />
      );
    case "date":
      return (
        <FormDateInput
          {...formField}
          format={others.format}
          formControl={formikInstance}
          label={InputLabel(formField, configurationFormFields)}
        />
      );
    case "datetime":
      return (
        <FormDateTimeInput
          {...formField}
          format={others.format}
          formControl={formikInstance}
          label={InputLabel(formField, configurationFormFields)}
        />
      );
    case "select":
      return (
        <FormSelectInput
          {...formField}
          formControl={formikInstance}
          label={InputLabel(formField, configurationFormFields)}
        />
      );
    case "multi-select":
      return (
        <FormMultiSelect
          {...formField}
          formControl={formikInstance}
          label={InputLabel(formField, configurationFormFields)}
        />
      );
    case "select-search":
      return (
        <FormSelectSearchInput
          {...formField}
          formControl={formikInstance}
          label={InputLabel(formField, configurationFormFields)}
        />
      );
    case "multi-select-search":
      return (
        <FormMultiSelectSearchInput
          {...formField}
          formControl={formikInstance}
          label={InputLabel(formField, configurationFormFields)}
        />
      );
    case "async-select-search":
      return (
        <FormAsyncSelectSearch
          {...formField}
          formControl={formikInstance}
          label={InputLabel(formField, configurationFormFields)}
        />
      );
    case "branch-field":
      return (
        <FormBranchField
          {...formField}
          formControl={formikInstance}
          label={InputLabel(formField, configurationFormFields)}
        />
      );
    case "checkbox":
      return (
        <FormCheckbox
          formControl={formikInstance}
          {...formField}
          label={InputLabel(formField, configurationFormFields)}
        />
      );

    case "radiobtn":
      return (
        <FormRadioBtn
          formControl={formikInstance}
          {...formField}
          label={InputLabel(formField, configurationFormFields)}
        />
      );
    case "textarea":
      return (
        <FormTextArea
          formControl={formikInstance}
          {...formField}
          label={InputLabel(formField, configurationFormFields)}
        />
      );

    case "text&select":
      return (
        <ConcatenatedTextSelect
          formControl={formikInstance}
          parentInput={(formField as any).parentInput}
          secondaryInput={(formField as any).secondaryInput}
        />
      );
    case "multtext":
      return (
        <MultiInputField
          {...formField}
          formControl={formikInstance}
          label={InputLabel(formField, configurationFormFields)}
        />
      );
    case "textAuto":
      return (
        <FormInputTextAutoCompletion
          {...formField}
          formControl={formikInstance}
          label={InputLabel(formField, configurationFormFields)}
        />
      );

    case "multi-select-check":
      return (
        <FormMultiSelectCheck
          {...formField}
          formControl={formikInstance}
          label={InputLabel(formField, configurationFormFields)}
        />
      );
    case "table-form-field":
      return (
        <TableFormField
          {...formField}
          formControl={formikInstance}
          label={InputLabel(formField, configurationFormFields)}
          readOnly={formField.isDisabled}
        />
      );
    case "geo-locator":
      return (
        <FormInputGeoLocator
          {...formField}
          formControl={formikInstance}
          label={InputLabel(formField, configurationFormFields)}
        />
      );
    case "multi-select-email":
      return (
        <FormMultiSelectEmail
          {...formField}
          formControl={formikInstance}
          label={InputLabel(formField, configurationFormFields)}
        />
      );

    default:
      return (
        <FormTextInput
          formControl={formikInstance}
          {...formField}
          label={InputLabel(formField, configurationFormFields)}
        />
      );
  }
};

export const GroupTypeFactory = (
  formField: IFormField,
  formikInstance: any,
  validationSchema: any,
  others: any,
  configurationFormFields?: IConfigurationFormField,
) => {
  switch (formField.uiType) {
    case "textselect":
      return (
        <ConcatenatedTextSelect
          formControl={formikInstance}
          parentInput={(formField as any).parentInput}
          secondaryInput={(formField as any).secondaryInput}
          {...formField}
        />
      );
    case "texttext":
      return (
        <InputGroupText
          formControl={formikInstance}
          parentInput={(formField as any).parentInput}
          secondaryInput={(formField as any).secondaryInput}
          {...formField}
        />
      );
    case "selectselect":
      return (
        <ConcatenatedSelectSelect
          formControl={formikInstance}
          parentInput={(formField as any).parentInput}
          secondaryInput={(formField as any).secondaryInput}
          {...formField}
        />
      );
    default:
      return <></>;
  }
};

const FormFactory: FC<IFormFactoryProps> = ({
  formFields,
  formikInstance,
  validationSchema,
  others,
  isCustomFormInstance = false,
  configurationFormFields,
  isHidden = false,
}) => {
  const groupedFields = formFields.filter((field) => field.isGrouped);
  const nonGroupedFields = formFields.filter((field) => !field.isGrouped);

  return (
    <Grid
      {...others}
      container
      rowSpacing={2}
      columnSpacing={{ xs: 1, sm: 2, md: 3 }}
      display={isHidden ? "none" : "flex"}
    >
      {nonGroupedFields.map((field: IFormField, index: number) => {
        return (
          <Grid
            sx={field.isHidden ? styles.hidden : {}}
            key={index}
            item
            {...field.uiBreakpoints}
          >
            {field.withVerification ? (
              <FlexBox sx={{ gap: 2, alignItems: "center" }}>
                {FormTypeFactory(
                  field,
                  isCustomFormInstance
                    ? formikInstance(field, index)
                    : formikInstance,
                  validationSchema,
                  { ...others, format: field.dateFormat },
                  configurationFormFields,
                )}
                <IsGranted
                  permissions={[
                    PERMISSIONS.VIEW_KYC_PHONE_NUMBER,
                    PERMISSIONS.VIEW_KYC_VALIDATE_NIN,
                  ]}
                >
                  <VerificationButton
                    formField={field}
                    formInstance={formikInstance}
                    verificationType={
                      field.verificationType as VerificationType
                    }
                    phoneType={field.phoneVerificationType}
                    displayType={BtnDisplayType.DETAILS}
                  />
                </IsGranted>
              </FlexBox>
            ) : (
              <>
                {FormTypeFactory(
                  field,
                  isCustomFormInstance
                    ? formikInstance(field, index)
                    : formikInstance,
                  validationSchema,
                  { ...others, format: field.dateFormat },
                  configurationFormFields,
                )}
              </>
            )}
          </Grid>
        );
      })}

      {groupedFields.map((field: IFormField, index: number) => {
        return (
          <Grid
            sx={field.isHidden ? styles.hidden : {}}
            key={index}
            item
            {...field.uiBreakpoints}
          >
            {GroupTypeFactory(
              field,
              isCustomFormInstance
                ? formikInstance(field, index)
                : formikInstance,
              validationSchema,
              { ...others, format: field.dateFormat },
              // configurationFormFields
            )}
          </Grid>
        );
      })}
    </Grid>
  );
};

export default FormFactory;

const styles = {
  hidden: {
    display: "none",
  },
};
